/**
 * LandedCostDialog.jsx — preview + finalize the per-line landed cost
 * allocation for a Container.
 *
 * Lifecycle:
 *  1. Operator opens dialog → we GET /landed-costs/preview to compute the
 *     spread without writing anything.
 *  2. They tweak the four extras + allocation method inline; each blur
 *     PATCHes the container's costs and re-fetches the preview so the
 *     numbers in the table stay live.
 *  3. They click "Finalize" → POST /landed-costs/finalize. Each affected
 *     SKU gets its landed_cost updated via moving weighted average.
 *  4. Once finalized the dialog locks the inputs. A "Reset" button lets the
 *     operator un-finalize so they can re-edit costs and re-finalize.
 *
 * Designed to gracefully no-op when the container has zero received qty
 * (the preview still computes against expected_qty as a forecast).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  previewLandedCosts, finalizeLandedCosts, resetLandedCosts, patchContainerCosts,
} from '../lib/api';
import { Coins, Calculator, Check, ArrowsClockwise, Warning } from '@phosphor-icons/react';

const METHODS = [
  { value: 'value', label: 'By value (qty × unit cost)' },
  { value: 'weight', label: 'By weight (kg)' },
  { value: 'volume', label: 'By volume (m³)' },
  { value: 'quantity', label: 'By quantity (equal per unit)' },
];

export default function LandedCostDialog({ open, onOpenChange, containerId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [extras, setExtras] = useState({ freight_cost: '', duty_cost: '', insurance_cost: '', other_cost: '', cost_allocation_method: 'value' });
  const [blend, setBlend] = useState(true);

  const load = async () => {
    if (!containerId) return;
    try {
      setLoading(true);
      const res = await previewLandedCosts(containerId);
      setData(res.data);
      setExtras({
        freight_cost: String(res.data.freight_cost ?? ''),
        duty_cost: String(res.data.duty_cost ?? ''),
        insurance_cost: String(res.data.insurance_cost ?? ''),
        other_cost: String(res.data.other_cost ?? ''),
        cost_allocation_method: res.data.method || 'value',
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && containerId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, containerId]);

  const finalized = !!data?.finalized;

  const totals = useMemo(() => {
    if (!data) return null;
    const lines = data.lines || [];
    return {
      total_qty: lines.reduce((s, l) => s + (l.qty_for_alloc || 0), 0),
      total_received: lines.reduce((s, l) => s + (l.received_qty || 0), 0),
      total_value: lines.reduce((s, l) => s + (l.value_basis || 0), 0),
      total_alloc: lines.reduce((s, l) => s + (l.extras_alloc || 0), 0),
      total_landed_value: lines.reduce(
        (s, l) => s + ((l.landed_cost_per_unit || 0) * (l.qty_for_alloc || 0)),
        0,
      ),
      // Promoted to top-level by the backend (iter-126b polish); fall back
      // to scanning rows for older payloads.
      fallback: data.fallback_method || lines[0]?.fallback_method || null,
    };
  }, [data]);

  const onSaveExtras = async () => {
    try {
      await patchContainerCosts(containerId, {
        freight_cost: parseFloat(extras.freight_cost) || 0,
        duty_cost: parseFloat(extras.duty_cost) || 0,
        insurance_cost: parseFloat(extras.insurance_cost) || 0,
        other_cost: parseFloat(extras.other_cost) || 0,
        cost_allocation_method: extras.cost_allocation_method,
      });
      toast.success('Costs saved · re-allocating');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const onFinalize = async () => {
    if (!window.confirm(
      'Finalize landed costs?\n\nEach affected SKU will have its landed_cost updated using a moving weighted average against current on-hand inventory. You can reset and re-finalize if costs change.'
    )) return;
    try {
      setSubmitting(true);
      const res = await finalizeLandedCosts(containerId, { blend_existing_qty: blend });
      toast.success('Landed costs finalized', {
        description: `${res.data.updated_skus?.length || 0} SKU(s) updated.`,
      });
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Finalize failed');
    } finally {
      setSubmitting(false);
    }
  };

  const onReset = async () => {
    if (!window.confirm(
      'Reset finalized state?\n\nThis re-opens the cost inputs so you can correct them and re-finalize. Existing SKU landed_cost values are NOT rolled back — re-finalizing will continue blending from the SKU\'s current value.'
    )) return;
    try {
      await resetLandedCosts(containerId);
      toast.success('Reset · costs are editable again');
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border border-border rounded-none max-w-4xl" data-testid="landed-cost-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Coins size={18} weight="duotone" className="text-amber-600" />
            Landed Costs · {data?.container_number || ''}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Spread freight + duty + insurance + other across the container's lines, then finalize to update each SKU's landed_cost.
            {finalized && <span className="ml-2 text-emerald-700 font-bold">FINALIZED</span>}
          </DialogDescription>
        </DialogHeader>

        {loading || !data ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading preview…</div>
        ) : (
          <div className="space-y-4">
            {/* Cost extras */}
            <div className="border border-border p-3 bg-muted/30">
              <div className="grid grid-cols-4 gap-2">
                {[
                  ['freight_cost', 'Freight'],
                  ['duty_cost', 'Duty'],
                  ['insurance_cost', 'Insurance'],
                  ['other_cost', 'Other'],
                ].map(([k, lbl]) => (
                  <div key={k}>
                    <Label className="overline text-[10px]">{lbl} ($)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min={0}
                      value={extras[k]}
                      onChange={(e) => setExtras((p) => ({ ...p, [k]: e.target.value }))}
                      disabled={finalized}
                      data-testid={`landed-${k.replace('_cost', '')}-input`}
                      className="font-mono"
                    />
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2 items-end">
                <div className="flex-1">
                  <Label className="overline text-[10px]">Allocation method</Label>
                  <select
                    value={extras.cost_allocation_method}
                    onChange={(e) => setExtras((p) => ({ ...p, cost_allocation_method: e.target.value }))}
                    disabled={finalized}
                    data-testid="landed-method-select"
                    className="w-full h-9 px-2 border border-border bg-background text-sm rounded-none"
                  >
                    {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSaveExtras}
                  disabled={finalized}
                  data-testid="landed-save-extras-btn"
                  className="rounded-none"
                >
                  <Calculator size={14} weight="bold" className="mr-2" /> Recalculate
                </Button>
              </div>
            </div>

            {/* Allocation table */}
            <div className="border border-border">
              <table className="w-full text-xs">
                <thead className="bg-muted text-[10px] uppercase tracking-wider">
                  <tr>
                    <th className="text-left p-2">SKU</th>
                    <th className="text-right p-2">Qty</th>
                    <th className="text-right p-2">Unit Cost</th>
                    <th className="text-right p-2">Value</th>
                    <th className="text-right p-2">Alloc</th>
                    <th className="text-right p-2">Landed/u</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {(data.lines || []).map((l) => (
                    <tr key={l.line_id} className="border-t border-border" data-testid={`landed-row-${l.sku_code || l.line_id}`}>
                      <td className="p-2">
                        <div className="font-bold">{l.sku_code || '—'}</div>
                        <div className="text-[10px] text-muted-foreground truncate font-sans">{l.description}</div>
                      </td>
                      <td className="text-right p-2">
                        {l.qty_for_alloc}
                        {(l.received_qty || 0) === 0 && (l.expected_qty || 0) > 0 && (
                          <div className="text-[9px] text-amber-600">expected</div>
                        )}
                      </td>
                      <td className="text-right p-2">${(l.unit_cost || 0).toFixed(2)}</td>
                      <td className="text-right p-2">${(l.value_basis || 0).toFixed(2)}</td>
                      <td className="text-right p-2 text-amber-700">${(l.extras_alloc || 0).toFixed(2)}</td>
                      <td className="text-right p-2 font-bold">${(l.landed_cost_per_unit || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-muted/50 font-mono text-xs border-t-2 border-border">
                  <tr>
                    <td className="p-2 font-bold uppercase tracking-wider text-[10px]">Totals</td>
                    <td className="text-right p-2">{totals?.total_qty}</td>
                    <td className="text-right p-2 text-muted-foreground">—</td>
                    <td className="text-right p-2">${(totals?.total_value || 0).toFixed(2)}</td>
                    <td className="text-right p-2 text-amber-700">${(totals?.total_alloc || 0).toFixed(2)}</td>
                    <td className="text-right p-2 font-bold">${(totals?.total_landed_value || 0).toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {totals?.fallback && (
              <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-300 text-xs text-amber-900">
                <Warning size={14} weight="bold" className="mt-0.5 shrink-0" />
                <div>
                  <strong>Fell back to {totals.fallback}</strong> — the chosen method had a zero basis (eg every SKU has weight=0). Set per-unit weight/volume on the SKUs to use the chosen method directly.
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-border pt-3">
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={blend}
                  onChange={(e) => setBlend(e.target.checked)}
                  disabled={finalized}
                  data-testid="landed-blend-checkbox"
                />
                <span>
                  <strong>Blend with existing on-hand</strong> (moving weighted average) —
                  uncheck to overwrite each SKU's landed_cost with this receipt's value directly.
                </span>
              </label>
              <div className="flex gap-2">
                {finalized ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={onReset}
                    data-testid="landed-reset-btn"
                    className="rounded-none"
                  >
                    <ArrowsClockwise size={14} weight="bold" className="mr-2" /> Reset
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={onFinalize}
                    disabled={submitting || (totals?.total_received || 0) === 0}
                    data-testid="landed-finalize-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-none"
                  >
                    <Check size={14} weight="bold" className="mr-2" />
                    {submitting ? 'Finalizing…' : 'Finalize · Update SKUs'}
                  </Button>
                )}
              </div>
            </div>
            {(totals?.total_received || 0) === 0 && !finalized && (
              <p className="text-[11px] text-muted-foreground">
                Receive at least one line before finalizing — finalize uses the actual received quantity, not the expected qty shown above.
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
